import React from "react";
import { Field, FormRenderProps } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { FiSend, FiFileText, FiTag } from "react-icons/fi";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import ShadowCard from "../../../components/common/ShadowCard";
import IconComponent from "../../../components/common/IconComponent";
import { requiredValidator } from "../../../components/formFields/CommonValidator";

const SendIcon = FiSend as any;
const FileTextIcon = FiFileText as any;
const TagIcon = FiTag as any;

interface EmailComposerCardProps {
  stateOptions?: { value: number; label: string }[];
  emailTemplateOptions: { value: number; label: string; raw: any }[];
  selectedTemplate: any;
  activeVariables: number[];
  customSubject?: string;
  selectedCount: number;
  sending: boolean;
  isSendAccess: boolean;
  options: { label: string; value: string }[];
  onStateChange?: (event: any) => void;
  onTemplateChange: (event: any, formRenderProps: FormRenderProps) => void;
  onSubjectChange?: (val: string, formRenderProps: FormRenderProps) => void;
  onGetSuggestions?: (
    e?: any,
    formRenderProps?: FormRenderProps,
    variableNumber?: number,
  ) => void;
  onSubmit: (values: any) => void;
  getFormValues: (formRenderProps: FormRenderProps) => any;
  formRenderProps: FormRenderProps;
}

export const EmailComposerCard: React.FC<EmailComposerCardProps> = ({
  emailTemplateOptions,
  selectedTemplate,
  activeVariables,
  selectedCount,
  sending,
  isSendAccess,
  options,
  onTemplateChange,
  onGetSuggestions,
  onSubmit,
  getFormValues,
  formRenderProps,
}) => {
  const isSendDisabled =
    sending || !selectedTemplate || selectedCount === 0 || !isSendAccess;

  return (
    <ShadowCard
      style={{
        padding: "18px",
        borderRadius: "16px",
        border: "1px solid #cdddf1ff",
        backgroundColor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* Card Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          paddingBottom: "10px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <div
          style={{
              padding: "6px",
              borderRadius: "8px",
              backgroundColor: "#eff6ff",
              color: "#174583",
              display: "flex",
              alignItems: "center",
          }}
        >
          <IconComponent icon={FileTextIcon} style={{ fontSize: "18px" }} />
        </div>
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: "15px",
              fontWeight: 700,
              color: "#1e293b",
            }}
          >
            Email Configuration
          </h3>
          <p
            style={{ margin: "2px 0 0 0", fontSize: "11px", color: "#64748b" }}
          >
            Configure template and dynamic variable tags
          </p>
        </div>
      </div>

      {/* Email Template Dropdown */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Field
          name="TemplateID"
          label="Select Email Template"
          placeholder="Choose Email Template..."
          component={FormSelectionField}
          options={emailTemplateOptions}
          validator={requiredValidator}
          astrike={true}
          onChange={(e: any) => onTemplateChange(e, formRenderProps)}
        />
      </div>

      {/* Dynamic Variables Section */}
      {selectedTemplate && activeVariables.length > 0 && (
        <div
          style={{
            backgroundColor: "#ffffffff",
            borderRadius: "12px",
            padding: "14px",
            border: "1px solid #e2e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconComponent
              icon={TagIcon}
              style={{ color: "#3b82f6", fontSize: "15px" }}
            />
            <span
              style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}
            >
              Dynamic Variables ({activeVariables.length})
            </span>
          </div>

          {activeVariables.map((varNum: number) => (
            <Field
              key={varNum}
              name={`Variable${varNum}`}
              label={`Variable {{${varNum}}}`}
              placeholder="Choose variable mapping or type value..."
              component={FormSelectionField}
              allowCustom={true}
              options={
                formRenderProps.valueGetter(`variable_options_${varNum}`) ||
                options
              }
              validator={requiredValidator}
              astrike={true}
              customOnFilterChange={(e: any) =>
                onGetSuggestions?.(e, formRenderProps, varNum)
              }
            />
          ))}
        </div>
      )}

      {/* Send Bulk Email Button */}
      <div style={{ marginTop: "8px" }}>
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onSubmit(getFormValues(formRenderProps));
          }}
          disabled={isSendDisabled}
          style={{
            width: "100%",
            height: "46px",
            borderRadius: "10px",
            fontWeight: 700,
            fontSize: "14px",
            background: isSendDisabled
              ? "#94a3b8"
              : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
            color: "#ffffff",
            border: "none",
            boxShadow: isSendDisabled
              ? "none"
              : "0 4px 14px rgba(37, 99, 235, 0.35)",
            cursor: isSendDisabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            transition: "all 0.2s ease",
          }}
        >
          <IconComponent icon={SendIcon} style={{ fontSize: "16px", marginRight: "12px" }} />
          <span>
            {sending
              ? "Sending Emails..."
              : `Send Bulk Email ${selectedCount > 0 ? `(${selectedCount} Contacts)` : ""}`}
          </span>
        </Button>
      </div>
    </ShadowCard>
  );
};
